import { useEffect } from 'react';
import { clearAuthAndRedirect, useAuth } from '@aimsos/auth';
import { InvitationRoutes } from '@aimsos/people-access';
import { Skeleton, StatusMessage } from '@aimsos/ui';
import { TriangleAlert } from 'lucide-react';
import { usePeopleAccessI18n } from './usePeopleAccessI18n.js';

/**
 * PUBLIC mount of the recipient-side invitation screens (`/invitations/:token`, CT-5567).
 *
 * Rendered outside `ProtectedRoute`: the recipient opens the emailed link before having a
 * session. Authentication is read from the unified `useAuth()` and passed in; the screens
 * send an unauthenticated recipient to `/login?next=<this link>` when they confirm, and only
 * accept once signed in. No Shell layout or workspace content is rendered here.
 */
const InvitationsEmbed = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { ready, failed } = usePeopleAccessI18n();

  // The lazy clients read the API base URL from here, with or without a session (the link is
  // looked up before sign-in).
  useEffect(() => {
    window.__AIMS_SHELL__ = { mounted: true, apiBaseUrl: import.meta.env.VITE_API_BASE_URL };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, []);

  if (failed) {
    // The translations are what failed to load, so this one fallback cannot use them.
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <StatusMessage
          icon={TriangleAlert}
          title="We could not load this page"
          subtitle="Reload the page to try again."
        />
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg items-center justify-center p-8">
        <Skeleton height="160px" borderRadius="12px" />
      </div>
    );
  }

  return (
    <InvitationRoutes
      isAuthenticated={isAuthenticated}
      authLoading={isLoading}
      signedInEmail={user?.email ?? null}
      onSignOut={clearAuthAndRedirect}
    />
  );
};

export default InvitationsEmbed;
